// Pure helper functions for the Daily Payment Processing screen.
// Kept free of redux/react so they're easy to unit test in isolation.
import dayjs from 'dayjs'
import { formatBusinessDate } from '../../../utils/date'
import { todayBusinessKey } from '../shared/presetRanges'
import { labelMap, shortLabelMap } from '../../../constants/gatewayRegistry'

// The one quick-filter key with special meaning outside the QUICK_FILTERS/
// matchesFilter mapping (it doubles as an implicit "reveal skipped rows"
// signal — see effectiveShowSkipped in DailyProcessing.jsx). Co-located here,
// with QUICK_FILTERS, as the single source of truth for that literal.
export const SKIPPED_FILTER_KEY = 'skipped'

export const QUICK_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'ready', label: 'Ready' },
  { key: 'needsReview', label: 'Needs Review' },
  { key: 'bounced', label: 'Bounced' },
  { key: 'retry', label: 'Retry' },
  { key: SKIPPED_FILTER_KEY, label: 'Skipped' },
]

// Slightly clearer filter labels than the raw chip keys (used by the chip row).
export const SIDEBAR_FILTER_LABELS = {
  all: 'All',
  ready: 'Ready',
  needsReview: 'Needs Review',
  bounced: 'Previously Bounced',
  retry: 'Retry',
  skipped: 'Skipped',
}

// Human labels for the specific payment gateways AND Mint sub-processors (must
// match the backend's GATEWAY_LABELS). Used by the per-row processor dropdown +
// summary breakdown. The per-row dropdown normally uses each availableGateways
// entry's own `label`; this map is the fallback when composing a label from a
// bare composite value (e.g. in the summary breakdown).
export const GATEWAY_LABELS = {
  // DERIVED from src/constants/gatewayRegistry.js — the same source of truth the backend's
  // GATEWAY_LABELS derives from, so a new gateway can never be missing here.
  ...labelMap(),
  // Mint sub-processors (the part after the ':' in a 'mint:<processor>' value). These are NOT
  // gateways and are deliberately not registry entries — they are NMI underlying-processor MIDs
  // declared in the backend's config.mint.processors.
  maverick: 'Maverick',
  humboldt: 'Humboldt',
  kurv: 'Kurv',
  payliance: 'Payliance',
}

export const gatewayLabel = (value) => GATEWAY_LABELS[value] || value || '—'

// WS 1281166 suggestion-source chip. WHICH layer of the shared routing rule produced the
// suggestion (`routeSource` on bulk rows and gateway options, the backend's vocabulary
// verbatim): 'manual' and 'stored' are both account-level configuration, 'clamp' means
// nothing chose the gateway and the system fell back to Mint. Unknown/absent sources map
// to null so rows from an older backend simply render no chip.
export const ROUTE_SOURCE_LABELS = {
  'company-default': 'Company default',
  'first-n': 'First N',
  stored: 'Account',
  manual: 'Account',
  clamp: 'System default',
}

export const routeSourceLabel = (source) => ROUTE_SOURCE_LABELS[source] || null

/**
 * Split a composite gateway <Select> value into its gateway + processor parts.
 *   'mint:maverick' -> { gateway: 'mint', processor: 'maverick' }
 *   'greenMoney'    -> { gateway: 'greenMoney', processor: null }
 *   'mint'          -> { gateway: 'mint', processor: null }
 * A plain (non-Mint or unconfigured) value has no ':' and yields a null processor,
 * so callers stay backward-compatible with the pre-processor value shape.
 */
export const parseGatewayValue = (value) => {
  if (typeof value !== 'string' || !value) return { gateway: undefined, processor: null }
  const [gateway, processor] = value.split(':')
  return { gateway, processor: processor || null }
}

/**
 * Human label for a composite gateway value:
 *   'mint:maverick' -> 'Mint · Maverick'
 *   'greenMoney'    -> 'green.money'
 *   'mint'          -> 'Mint'
 * Used by the summary breakdown as a fallback when there's no availableGateways
 * entry (which carries its own backend-provided `label`) to derive from.
 */
export const formatGatewayValue = (value) => {
  if (!value) return '—'
  const { gateway, processor } = parseGatewayValue(value)
  const gatewayName = GATEWAY_LABELS[gateway] || gateway
  if (!processor) return gatewayName
  const processorName = GATEWAY_LABELS[processor] || processor
  return `${gatewayName} · ${processorName}`
}

/**
 * Initials for the client avatar, e.g. "Alice Smith" -> "AS".
 */
// "Payment #N of M", plus the attempt of a rescheduled declined payment (WS 2074858), which keeps
// its number.
export const paymentLabel = (row) =>
  `Payment #${row?.pmt}${row?.totalPayments ? ` of ${row.totalPayments}` : ''}${row?.attempt ? ` – Attempt ${row.attempt}` : ''}`

export const getInitials = (name) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/**
 * Number of processing attempts for a row, as provided by the API.
 * Falls back to 0 if the field is missing/null rather than guessing from
 * `previousBounce` — the bulk-candidates API now always sends `attempts`.
 */
export const getAttempts = (row) => {
  if (typeof row?.attempts === 'number') return row.attempts
  return 0
}

export const isSkipped = (row, overrides = {}) => !!overrides?.[row?.paymentId]?.skip

export const getAllowBounced = (row, overrides = {}) => !!overrides?.[row?.paymentId]?.allowBounced

/**
 * The composite gateway value that will actually process this row (post-override),
 * ignoring skip. Falls back to the backend-provided recommendedGateway when the
 * operator hasn't overridden it. Returns a composite value (e.g. 'mint:maverick' |
 * 'greenMoney' | 'mint') or undefined. Parse it with parseGatewayValue to split the
 * gateway from the Mint sub-processor.
 */
export const getEffectiveGateway = (row, overrides = {}) => {
  const override = overrides?.[row?.paymentId]
  return override?.gateway || row?.recommendedGateway || undefined
}

/**
 * The current value for the per-row processor <Select>: 'skip', or a specific
 * gateway value (the override if set, else the recommended gateway).
 */
export const getEffectiveSelectValue = (row, overrides = {}) => {
  const override = overrides?.[row?.paymentId]
  if (override?.skip) return 'skip'
  return override?.gateway || row?.recommendedGateway || ''
}

/**
 * Gateway options to show for a row: the backend's availableGateways when present,
 * otherwise a single-entry fallback from recommendedGateway so the dropdown still
 * works before the backend ships the field.
 */
export const getRowGatewayOptions = (row) => {
  if (Array.isArray(row?.availableGateways) && row.availableGateways.length) {
    return row.availableGateways
  }
  if (row?.recommendedGateway) {
    return [{ value: row.recommendedGateway, label: gatewayLabel(row.recommendedGateway), recommended: true }]
  }
  return []
}

// ============================================
// SELECT BY PROCESSOR
// The "Select by processor" control groups the currently-visible, selectable rows by the
// gateway they will actually submit with (row.resolvedGateway) and bulk-selects the Ready
// rows of one processor. Composite Mint sub-processor values ('mint:<processor>') collapse
// onto a single 'mint' group, so choosing "Mint" selects every Ready Mint row regardless of
// sub-processor.
// ============================================

// Gateway-level labels for the processor groups. DERIVED (shortLabelMap): deliberately shorter
// than GATEWAY_LABELS.manual's parenthetical form ("Manual (mark processed)") to keep the compact
// "Manual (7)" menu-item form readable. Was hand-maintained and MISSING lawpay on dev.
export const PROCESSOR_GROUP_LABELS = shortLabelMap()

export const processorGroupLabel = (gatewayKey) => PROCESSOR_GROUP_LABELS[gatewayKey] || gatewayLabel(gatewayKey)

/**
 * The processor GROUP key for a row: its resolvedGateway normalized to the bare gateway id,
 * so every Mint sub-processor ('mint:maverick', 'mint:humboldt', ...) buckets to 'mint'.
 * Returns null when the row has no resolvedGateway, so it falls outside every group.
 */
export const rowProcessorKey = (row) => parseGatewayValue(row?.resolvedGateway).gateway || null

/**
 * Processor groups present among the given (already filtered/visible) rows, restricted to
 * Ready rows — the only rows this control can bulk-select (Needs-Review rows can't be
 * bulk-processed). Returns [{ key, label, count }], sorted alphabetically by (lower-cased)
 * label so the menu keeps a stable position as daily volumes shift. Empty when no Ready rows
 * are present.
 */
export const buildProcessorGroups = (rows = []) => {
  const byKey = new Map()
  rows.forEach((row) => {
    if (row?.status !== 'Ready') return
    const key = rowProcessorKey(row)
    if (!key) return
    byKey.set(key, (byKey.get(key) || 0) + 1)
  })
  return [...byKey.entries()]
    .map(([key, count]) => ({ key, label: processorGroupLabel(key), count }))
    .sort((a, b) => {
      const la = a.label.toLowerCase()
      const lb = b.label.toLowerCase()
      return la < lb ? -1 : la > lb ? 1 : 0
    })
}

/**
 * Selection map that selects the Ready rows of a single processor and deselects everything
 * else, mirroring the Ready-only rule of "Select all clean". setSelection replaces the whole
 * map, so other processors' rows (explicitly false here, or absent for rows outside `rows`)
 * are left unselected. MUST be built over the same row collection the groups were derived
 * from, or a group's displayed count won't match what clicking it selects.
 */
export const buildProcessorSelection = (rows = [], processorKey) => {
  const next = {}
  rows.forEach((row) => {
    next[row.paymentId] = row.status === 'Ready' && rowProcessorKey(row) === processorKey
  })
  return next
}

export const matchesFilter = (row, filterKey, overrides = {}) => {
  switch (filterKey) {
    case 'ready':
      return row?.status === 'Ready'
    case 'needsReview':
      return row?.status === 'Needs Review'
    case 'bounced':
      return !!row?.previousBounce
    case 'retry':
      return getAttempts(row) >= 2
    case 'skipped':
      return isSkipped(row, overrides)
    case 'all':
    default:
      return true
  }
}

export const filterRows = (rows = [], filterKey, overrides = {}) =>
  rows.filter((row) => matchesFilter(row, filterKey, overrides))

/**
 * Facet filters layered UNDER the quick-filter chips: payment method type (ACH/Card,
 * from the backend's billingMethod.type) and billing-plan status (active/inactive).
 * 'all' (or absent) matches everything, so both facets are opt-in.
 */
export const matchesPaymentType = (row, paymentType) => {
  if (!paymentType || paymentType === 'all') return true
  return row?.billingMethod?.type === paymentType
}

export const matchesPlanStatus = (row, planFilter) => {
  if (!planFilter || planFilter === 'all') return true
  return (row?.planStatus || '').toString().trim().toLowerCase() === planFilter
}

/**
 * Applies the facet filters (type + plan status). The quick-filter chips, their
 * counts and the table all read from this filtered set, so the chip numbers always
 * describe what selecting that chip would actually show.
 */
export const filterFacetRows = (rows = [], { paymentType = 'all', planFilter = 'all' } = {}) =>
  rows.filter((row) => matchesPaymentType(row, paymentType) && matchesPlanStatus(row, planFilter))

/**
 * Rows to actually render in the active queue: the given (already
 * quick-filtered) rows, minus anything the operator skipped via the per-row
 * "Skip" override — unless the "Skipped (n)" view is toggled on, in which
 * case skipped rows are shown alongside the rest.
 *
 * This is purely a client-side hide layered on top of the existing skip
 * override: the payment is never mutated server-side, so a skipped row keeps
 * its schedule and simply reappears (still skippable) on a future load.
 */
export const filterActiveRows = (rows = [], overrides = {}, { showSkipped = false } = {}) =>
  showSkipped ? rows : rows.filter((row) => !isSkipped(row, overrides))

/**
 * Live counts for each quick-filter chip. Every bucket EXCEPT "skipped" is
 * scoped to the non-skipped (visible) rows, since a skipped row is hidden
 * from the table by default (see filterActiveRows) — counting it anyway made
 * a chip like "Ready (5)" show a number higher than what the operator could
 * actually see. "skipped" itself stays a total over all rows, since that's
 * the count of everything the toggle would reveal.
 */
export const getFilterCounts = (rows = [], overrides = {}) => {
  const counts = {}
  QUICK_FILTERS.forEach(({ key }) => {
    counts[key] =
      key === SKIPPED_FILTER_KEY
        ? rows.filter((row) => matchesFilter(row, key, overrides)).length
        : rows.filter((row) => !isSkipped(row, overrides) && matchesFilter(row, key, overrides)).length
  })
  return counts
}

/**
 * Items to send to processBatch: selected rows, excluding anything the operator
 * marked "Skip (do not process)" in the per-row processor override.
 *
 * NOT-YET-DUE ROWS ARE DROPPED unless `allowFutureDue` is set (2026-07-28 incident: a
 * select-all over a range reaching today+3 charged 104 payments due 07/29-07/31 a day to
 * three days early). Opting in tags each future-dated item with `allowFutureDue: true`,
 * which is exactly what vlg-server's `not_yet_due` guard requires to let it through — so
 * the operator's confirmation, and nothing else, is what unlocks an early charge.
 * `todayKey` defaults to the business day so a caller that forgets to pass it still gets
 * the safe behavior rather than an ungated batch.
 */
export const buildProcessItems = (
  rows = [],
  selection = {},
  overrides = {},
  { todayKey = todayBusinessKey(), allowFutureDue = false } = {}
) =>
  rows
    .filter((row) => !!selection[row.paymentId] && !isSkipped(row, overrides))
    .filter((row) => allowFutureDue || !isFutureDueRow(row, todayKey))
    .map((row) => {
      const item = {
        paymentId: row.paymentId,
        allowBounced: getAllowBounced(row, overrides),
      }
      // Only the genuinely future-dated rows carry the flag — an on-or-before-today
      // payment needs no opt-in and must not claim one.
      if (isFutureDueRow(row, todayKey)) item.allowFutureDue = true
      // Only send `gateway`/`processor` when the operator explicitly overrode the
      // recommendation — omitting both lets the backend take its unchanged
      // default-resolution path. The override stores the composite value
      // ('mint:maverick', 'greenMoney', ...); split it into the flat fields the
      // backend expects. `processor` is only added for Mint sub-processor values.
      const chosen = overrides?.[row.paymentId]?.gateway
      if (chosen) {
        const { gateway, processor } = parseGatewayValue(chosen)
        item.gateway = gateway
        if (processor) item.processor = processor
      }
      return item
    })

/**
 * Aggregate stats over the currently selected rows: count, dollar amount,
 * average AI forecast, and an ACH/Card processor breakdown. Shared by the
 * bottom summary bar and the confirm drawer.
 */
export const computeSelectionSummary = (
  rows = [],
  selection = {},
  overrides = {},
  { todayKey = todayBusinessKey(), allowFutureDue = false } = {}
) => {
  const selectedRows = rows.filter((row) => !!selection[row.paymentId])
  // The headline count/amount MUST match the batch buildProcessItems actually
  // sends — which excludes skipped rows, and (unless the operator opted in) not-yet-due
  // rows. Otherwise the confirm drawer and summary bar promise "N payments, $X" while a
  // smaller batch is approved. The excluded future rows are reported separately below so
  // they're visible rather than silently missing.
  const futureRows = selectedRows.filter((row) => !isSkipped(row, overrides) && isFutureDueRow(row, todayKey))
  const processableRows = selectedRows
    .filter((row) => !isSkipped(row, overrides))
    .filter((row) => allowFutureDue || !isFutureDueRow(row, todayKey))
  const count = processableRows.length
  const amount = processableRows.reduce((sum, row) => sum + Math.max(0, (Number(row.amount) || 0) - (Number(row.amountPaid) || 0)), 0)

  const forecastRates = processableRows
    .map((row) => row?.forecast?.successRate)
    .filter((value) => typeof value === 'number')
  const avgForecast = forecastRates.length
    ? forecastRates.reduce((a, b) => a + b, 0) / forecastRates.length
    : null

  // Count selected (non-skipped) rows per effective composite gateway value, e.g.
  // { 'mint:maverick': 5, greenMoney: 4, sola: 2 }, alongside a value -> human-label
  // map derived from each row's availableGateways entry (falling back to a composed
  // label). The components render `${gatewayLabels[value]} ${count}` per entry.
  const gatewayBreakdown = {}
  const gatewayLabels = {}
  // Reads processableRows, not selectedRows: the breakdown describes the batch being sent,
  // so an excluded future-dated row must not inflate a gateway's count.
  processableRows.forEach((row) => {
    const value = getEffectiveGateway(row, overrides)
    if (!value) return
    gatewayBreakdown[value] = (gatewayBreakdown[value] || 0) + 1
    if (!gatewayLabels[value]) {
      const entry = (row.availableGateways || []).find((opt) => opt.value === value)
      gatewayLabels[value] = entry?.label || formatGatewayValue(value)
    }
  })

  const bouncedIncludedCount = selectedRows.filter((row) => row?.status === 'Needs Review').length
  const skippedCount = selectedRows.filter((row) => isSkipped(row, overrides)).length

  // Reported whether or not they're included, so the confirm drawer can always say
  // "N of your selected payments aren't due yet" instead of quietly dropping them.
  const futureDueCount = futureRows.length
  const futureDueAmount = futureRows.reduce(
    (sum, row) => sum + Math.max(0, (Number(row.amount) || 0) - (Number(row.amountPaid) || 0)),
    0
  )

  // LawPay Part B (Task 6): whether ANY row actually being confirmed (selected AND
  // not skipped — the same set buildProcessItems sends) has a backend-configured
  // backup gateway. Drives the ConfirmDrawer's one-line failover disclosure so an
  // operator approving a batch is never unaware a backup processor may auto-fire.
  const hasBackupGateway = processableRows.some((row) => !!row.backupGateway)

  return {
    count,
    amount,
    avgForecast,
    gatewayBreakdown,
    gatewayLabels,
    bouncedIncludedCount,
    skippedCount,
    futureDueCount,
    futureDueAmount,
    hasBackupGateway,
  }
}

/**
 * Color key ('success' | 'warning' | 'error' | 'default') for an AI forecast band.
 * Matched case-insensitively since the backend may send 'high' or 'High'.
 */
export const getBandColorKey = (band) => {
  const normalized = (band || '').toString().trim().toLowerCase()
  if (normalized === 'very high' || normalized === 'high') return 'success'
  if (normalized === 'medium') return 'warning'
  if (normalized === 'low') return 'error'
  return 'default' // Unknown / unmapped
}

/**
 * Color key for a last-payment status label.
 * NOTE: the documented vocabulary is Cleared / Bounced (NSF) / Declined. 'Paid'
 * is the pre-rename spelling of Cleared and is still accepted, so this holds
 * against a backend on either side of the rename. Anything outside the
 * documented set falls back to 'default' (muted) rather than guessing.
 */
export const getLastPaymentColorKey = (status) => {
  const normalized = (status || '').toString().trim().toLowerCase()
  if (normalized === 'cleared' || normalized === 'paid') return 'success'
  if (normalized === 'bounced (nsf)' || normalized === 'bounced' || normalized === 'declined') return 'error'
  return 'default'
}

export const getStatusColorKey = (status) => {
  if (status === 'Ready') return 'success'
  if (status === 'Needs Review') return 'warning'
  return 'default'
}

/**
 * Human label for a row's billing-plan status ('active' | 'inactive', as sent by
 * the backend). Capitalizes for display; falls back to EM_DASH when missing so
 * the badge never renders blank/undefined.
 */
export const formatPlanStatus = (planStatus) => {
  const normalized = (planStatus || '').toString().trim().toLowerCase()
  if (!normalized) return EM_DASH
  return normalized.charAt(0).toUpperCase() + normalized.slice(1)
}

/**
 * Color key for the Plan Status badge. Inactive-plan payments are deliberately
 * surfaced here for manual review (they're never auto-charged), so they get
 * 'error' to stand out; active plans get the quiet 'default' treatment since
 * they need no operator attention.
 */
export const getPlanStatusColorKey = (planStatus) => {
  const normalized = (planStatus || '').toString().trim().toLowerCase()
  if (normalized === 'inactive') return 'error'
  return 'default'
}

/**
 * Translates a processor <Select> value ('skip' | a gateway value like 'mint')
 * into the setOverride patch to dispatch for that row.
 */
export const buildOverridePatch = (selectValue) => {
  if (selectValue === 'skip') return { skip: true }
  return { gateway: selectValue, skip: false }
}

// The backend emits forecast/success rates on a 0–100 scale (round(100*cleared/total),
// band thresholds ≥85/70/50). These are ALREADY percentages — format as-is, do NOT
// multiply by 100 again (that produced values like "10000%").
export const formatPercent = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—'
  return `${Math.round(value)}%`
}

/**
 * Format a date value as e.g. "Apr 4, 2033". Delegates to the platform's business-time,
 * date-only-aware formatter (utils/date) so date-only values (plan start, last payment) —
 * business facts — render the Eastern calendar day for every viewer and never drift a day
 * for non-Eastern browsers. Returns the EM_DASH for null/undefined so cells never show a
 * raw ISO timestamp.
 */
export const EM_DASH = '—'
export const formatDate = (value) => {
  if (!value) return EM_DASH
  return formatBusinessDate(value, 'MMM D, YYYY')
}

// ============================================
// OVERDUE / BACKLOG VISIBILITY
// The To Process default range is overdue-inclusive (see presetRanges). These helpers
// derive the overdue backlog from the LOADED candidate rows — no extra fetch — so a
// payment due on a prior/non-business day is never silently hidden by a "today only" view.
// ============================================

// Due-date-dimension selection, layered alongside the status quick-filter and the
// type/plan facet filters. It is purely client-side over the already-loaded rows (never a
// refetch): the default overdue-inclusive range loads everything on-or-before today, and
// this narrows the visible slice.
export const DUE_FILTER_ALL = 'all'
export const DUE_FILTER_OVERDUE = 'overdue'

/**
 * The BUSINESS calendar day (America/New_York) a candidate row is due on, as a 'YYYY-MM-DD'
 * key — the same business-date projection used everywhere else on this screen
 * (formatBusinessDate), so a row buckets to the same day for every operator regardless of
 * browser timezone. Rows with no dueDate return null and fall outside every bucket.
 */
export const rowDueDateKey = (row) => (row?.dueDate ? formatBusinessDate(row.dueDate, 'YYYY-MM-DD') : null)

/**
 * A row is overdue when its business due-day is strictly before today's business day.
 * `todayKey` is the caller-supplied 'YYYY-MM-DD' business day (see todayBusinessKey) —
 * lexical string comparison of these keys orders dates correctly. Today's and any
 * future-dated rows (a wide range can include a few) are never overdue.
 */
export const isOverdueRow = (row, todayKey) => {
  const key = rowDueDateKey(row)
  return !!key && !!todayKey && key < todayKey
}

export const filterOverdueRows = (rows = [], todayKey) => rows.filter((row) => isOverdueRow(row, todayKey))

/**
 * A row is FUTURE-DUE (not yet due) when its business due-day is strictly AFTER today's.
 * The mirror of isOverdueRow, and the predicate the whole early-charge guard turns on:
 * charging one of these means taking a client's money before the day they agreed to.
 * Rows with no dueDate are never future-dated (nothing to be early about).
 */
export const isFutureDueRow = (row, todayKey) => {
  const key = rowDueDateKey(row)
  return !!key && !!todayKey && key > todayKey
}

export const filterFutureDueRows = (rows = [], todayKey) => rows.filter((row) => isFutureDueRow(row, todayKey))

/**
 * Matches a row against the due-date-dimension filter: 'all' (whole loaded range),
 * 'overdue' (business day < today), or a specific 'YYYY-MM-DD' business day (the backlog
 * banner's click-to-filter). Absent/'all' matches everything, so the filter is opt-in.
 */
export const matchesDueFilter = (row, dueFilter, todayKey) => {
  if (!dueFilter || dueFilter === DUE_FILTER_ALL) return true
  if (dueFilter === DUE_FILTER_OVERDUE) return isOverdueRow(row, todayKey)
  return rowDueDateKey(row) === dueFilter
}

export const filterDueRows = (rows = [], dueFilter, todayKey) =>
  rows.filter((row) => matchesDueFilter(row, dueFilter, todayKey))

/**
 * Per-business-day breakdown of the overdue backlog, newest overdue day first:
 * [{ dateKey, count, weekday, label }]. `dateKey` doubles as a due-filter value
 * (matchesDueFilter) so a banner chip click narrows the table to exactly that day.
 * `weekday`/`label` are derived from the already-resolved business calendar day, so they
 * need no timezone projection (the day was pinned in businessTimeZone upstream).
 */
export const buildOverdueBreakdown = (rows = [], todayKey) => {
  const byDay = new Map()
  rows.forEach((row) => {
    if (!isOverdueRow(row, todayKey)) return
    const key = rowDueDateKey(row)
    byDay.set(key, (byDay.get(key) || 0) + 1)
  })
  return [...byDay.entries()]
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .map(([dateKey, count]) => {
      const day = dayjs(dateKey, 'YYYY-MM-DD')
      return { dateKey, count, weekday: day.format('ddd'), label: day.format('ddd, MMM D') }
    })
}

/**
 * Per-business-day breakdown of the SELECTED batch, earliest day first:
 * [{ dateKey, count, amount, isFuture, label }].
 *
 * The confirm drawer renders this so an operator always sees WHICH due dates they are about
 * to charge — the 2026-07-28 incident's batch looked like an ordinary day's work in the UI
 * while actually spanning three future due dates. Skipped and unselected rows are excluded,
 * so it describes the real batch.
 */
export const buildDueDateBreakdown = (rows = [], selection = {}, overrides = {}, todayKey) => {
  const byDay = new Map()
  rows
    .filter((row) => !!selection[row.paymentId] && !isSkipped(row, overrides))
    .forEach((row) => {
      const dateKey = rowDueDateKey(row)
      if (!dateKey) return
      const entry = byDay.get(dateKey) || { dateKey, count: 0, amount: 0, isFuture: isFutureDueRow(row, todayKey) }
      entry.count += 1
      entry.amount += Math.max(0, (Number(row.amount) || 0) - (Number(row.amountPaid) || 0))
      byDay.set(dateKey, entry)
    })
  return [...byDay.values()]
    .sort((a, b) => (a.dateKey < b.dateKey ? -1 : 1))
    .map((entry) => ({ ...entry, label: dayjs(entry.dateKey, 'YYYY-MM-DD').format('ddd, MMM D') }))
}
